"use client";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Card } from "@/core/components/ui/card";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import type { TooltipProps } from "recharts";
import type { ChartRendererProps } from "../types";
import {
  DEFAULT_PALETTE,
  seriesColor,
  categoryColor,
  formatAxisLabel,
} from "../utils/chartUtils";

// Tokens visuais alinhados ao design system (mesmo look dos gráficos do dashboard).
const AXIS_TICK = { fontSize: 11, fill: "#5C5C5C" }; // darkgray-400
const GRID_STROKE = "#EBEBEB"; // lightgray-400
const AXIS_LINE = { stroke: "#EBEBEB" };
const BAR_RADIUS: [number, number, number, number] = [6, 6, 0, 0];
const BAR_MAX_SIZE = 44;

export function ChartRender({
  spec,
  palette = DEFAULT_PALETTE,
  randomize = false,
  showTitle = true,
}: ChartRendererProps) {
  const { type, title, x, series, stacked, note, horizontal } = spec;
  const { t } = useTranslation(["core"]);

  const seriesColorMap = seriesColor(series, palette, randomize);
  const categoryColorMap = categoryColor(x, palette, randomize);

  const data = React.useMemo(
    () =>
      x.map((label, i) =>
        series.reduce((acc, s) => ({ ...acc, [s.name]: s.data[i] ?? null }), {
          x: label,
        }),
      ),
    [x, series],
  );

  const valueUnit = series[0]?.unit;
  const formatValue = React.useCallback(
    (value: number | string): string => {
      if (value === null || value === undefined) return "";
      if (!valueUnit) return `${value}`;
      return valueUnit === "%" ? `${value}%` : `${value} ${valueUnit}`;
    },
    [valueUnit],
  );

  // Traduz mês/dia da semana (dados vêm em inglês do datalake) p/ o idioma do cliente.
  const formatLabel = React.useCallback(
    (label: string | number) => formatAxisLabel(label, t),
    [t],
  );

  // Tooltip estilizado (cartão arredondado, sombra suave, cores do DS).
  const renderTooltip = React.useCallback(
    ({ active, payload, label }: TooltipProps<number, string>) => {
      if (!active || !payload?.length) return null;
      return (
        <div className="overflow-hidden rounded-[12px] border border-lightgray-400 bg-white shadow-md">
          <div className="bg-lightgray-200 px-3 py-1.5 text-xs font-medium text-darkgray-900">
            {formatLabel(label ?? "")}
          </div>
          <div className="flex flex-col gap-1 px-3 py-2">
            {payload.map((p, i) => (
              <div
                key={`${p.name ?? i}`}
                className="flex items-center gap-2 text-xs"
              >
                <span
                  className="size-2 shrink-0 rounded-full"
                  style={{ backgroundColor: p.color }}
                />
                <span className="text-darkgray-500">{p.name}</span>
                <span className="ml-auto font-semibold text-darkgray-900">
                  {formatValue(p.value ?? "")}
                </span>
              </div>
            ))}
          </div>
        </div>
      );
    },
    [formatLabel, formatValue],
  );

  const legendStyle = { fontSize: 12, color: "#5C5C5C" };

  let chartEl: React.ReactElement;

  if (type === "bar") {
    chartEl = (
      <BarChart
        data={data}
        layout={horizontal ? "vertical" : "horizontal"}
        margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
      >
        <CartesianGrid
          vertical={false}
          strokeDasharray="3 3"
          stroke={GRID_STROKE}
        />
        {horizontal ? (
          <>
            <XAxis
              type="number"
              tickFormatter={formatValue}
              tick={AXIS_TICK}
              tickLine={false}
              axisLine={AXIS_LINE}
            />
            <YAxis
              dataKey="x"
              type="category"
              width={150}
              tickFormatter={formatLabel}
              tick={AXIS_TICK}
              tickLine={false}
              axisLine={AXIS_LINE}
            />
          </>
        ) : (
          <>
            <XAxis
              dataKey="x"
              tickFormatter={formatLabel}
              tick={AXIS_TICK}
              tickLine={false}
              axisLine={AXIS_LINE}
            />
            <YAxis
              tickFormatter={formatValue}
              tick={AXIS_TICK}
              tickLine={false}
              axisLine={false}
            />
          </>
        )}
        <Tooltip cursor={{ fill: "#00000008" }} content={renderTooltip} />
        {series.length > 1 && <Legend wrapperStyle={legendStyle} />}
        {series.map((s) => {
          const color = seriesColorMap.get(s.name)!;
          return (
            <Bar
              key={s.name}
              dataKey={s.name}
              stackId={stacked ? "a" : undefined}
              fill={color}
              radius={BAR_RADIUS}
              maxBarSize={BAR_MAX_SIZE}
            />
          );
        })}
      </BarChart>
    );
  } else if (type === "line") {
    chartEl = (
      <LineChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid
          vertical={false}
          strokeDasharray="3 3"
          stroke={GRID_STROKE}
        />
        <XAxis
          dataKey="x"
          tickFormatter={formatLabel}
          tick={AXIS_TICK}
          tickLine={false}
          axisLine={AXIS_LINE}
        />
        <YAxis
          tickFormatter={formatValue}
          tick={AXIS_TICK}
          tickLine={false}
          axisLine={false}
        />
        <Tooltip content={renderTooltip} />
        {series.length > 1 && <Legend wrapperStyle={legendStyle} />}
        {series.map((s) => {
          const color = seriesColorMap.get(s.name)!;
          return (
            <Line
              key={s.name}
              type="monotone"
              dataKey={s.name}
              stroke={color}
              strokeWidth={2.5}
              dot={false}
              activeDot={{ r: 4, strokeWidth: 0 }}
            />
          );
        })}
      </LineChart>
    );
  } else {
    const pieData = x.map((label, i) => ({
      name: label,
      value: series[0]?.data[i] ?? 0,
    }));

    chartEl = (
      <PieChart>
        <Pie
          data={pieData}
          dataKey="value"
          nameKey="name"
          innerRadius={type === "doughnut" ? 62 : 0}
          outerRadius={92}
          paddingAngle={2}
          stroke="#fff"
          strokeWidth={2}
        >
          {pieData.map((d, i) => {
            const color =
              categoryColorMap.get(String(d.name)) ??
              palette[i % palette.length];
            return <Cell key={i} fill={color} />;
          })}
        </Pie>
        <Tooltip content={renderTooltip} />
        <Legend
          wrapperStyle={legendStyle}
          formatter={(value: string) => formatLabel(value)}
        />
      </PieChart>
    );
  }

  return (
    <Card className="rounded-xl border border-lightgray-400 p-4 shadow-sm">
      {showTitle && title && (
        <div className="mb-3 max-w-[90%] text-sm font-semibold text-darkgray-900">
          {title}
        </div>
      )}
      <div style={{ width: "100%", height: 300 }}>
        <ResponsiveContainer width="100%" height="100%">
          {chartEl}
        </ResponsiveContainer>
      </div>
      {note && <div className="mt-2 text-xs text-darkgray-400">{note}</div>}
    </Card>
  );
}
